import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { ProjectService } from 'app/services/project.service';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { MaterialModule } from 'app/core/material/material.module';
import { CommonModule } from '@angular/common';
// import { ColorPickerModule } from 'ngx-color-picker';
// import { TeamsService } from '../../../people/services/teams.service';
import { Team } from 'app/models/organization/team/team';
import { ProjectRoles } from 'app/models/project/project';
// import { UsersService } from 'app/services/users.service';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { MembersFormComponent } from '../members-form/members-form.component';
import { AutocompleteSelectTeamsComponent } from 'app/shared/components/autocomplete-select-teams/autocomplete-select-teams.component';
import { NgxColorsModule } from 'ngx-colors';

@Component({
  selector: 'tm-new-project',
  standalone: true,
  imports: [MaterialModule, CommonModule, ReactiveFormsModule, NgxColorsModule, AutocompleteSelectTeamsComponent, MembersFormComponent],
  templateUrl: './new-project.component.html',
  styleUrls: ['./new-project.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewProjectComponent implements OnDestroy, OnInit {
  orientation = 'vertical';
  private readonly destroy$ = new Subject<void>();
  readonly submitting$ = new BehaviorSubject<boolean | void>(false);
  private projectUsers = [];
  readonly projectForm: FormGroup = this.fb.group({
    general: this.fb.group({
      name: ['', [Validators.required]],
      color: '',
      description: null,
    }),
  });

  get members() {
    return (this.projectForm.get('members') as never) as FormGroup<{ members: FormControl<Array<{ id: string, isManager: boolean }>>, managers: FormControl<Array<{ id: string, isManager: boolean }>> }>;
  }

  get membersFC() {
    return this.members.get('members');
  }
  get managersFC() {
    return this.members.get('managers');
  }

  set color(v: string) {
    this.general.get('color').setValue(v, { emitEvent: false });
  }

  get color(): string {
    return this.general.get('color').value;
  }

  get general(): FormGroup {
    return this.projectForm.get('general') as FormGroup;
  }

  get users(): FormControl {
    return this.projectForm.get('users') as FormControl;
  }

  get teams(): FormControl {
    return this.projectForm.get('users') as FormControl;
  }

  constructor(
    private readonly dialogRef: MatDialogRef<NewProjectComponent>,
    private readonly fb: FormBuilder,
    private readonly projectSerivice: ProjectService,
    private readonly _fuseMediaWatcherService: FuseMediaWatcherService,
    private readonly chr: ChangeDetectorRef,) {
  }

  initMembers() {
    this.members.patchValue({
      members: [],
      managers: [],
    });
  }

  isDisabledUser(id: string) {
    return this.projectUsers.findIndex(userId => userId === id) > -1;
  }

  selectAll(teams: Array<Team>) {
    const teamsIds = teams.map(team => team.id);
    this.projectForm.get('teams').setValue(teamsIds);
  }

  addProject() {
    this.submitting$.next(true);
    const members = [
      ...(this.membersFC.value || []).map(member => ({ id: member.id, role: ProjectRoles.Member })),
      ...(this.managersFC.value || []).map(manager => ({ id: manager.id, role: ProjectRoles.Manager }))
    ];
    const groups = this.projectForm.get('teams')?.value?.map(team => team?.id) || [];
    const project = {
      name: this.general.get('name').value,
      description: this.general.get('description').value || null,
      color: parseInt((this.general.get('color').value as string).replace('#', ''), 16) || 0,
      users: members,
      groups
    };
    this.projectSerivice.addProjects(project).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: ({ id }) => {
        this.submitting$.next(false);
        this.dialogRef.close(id);
      },
      error: () => {
        this.submitting$.next(false);
      }
    });
  }

  ngOnInit(): void {
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe(({ matchingAliases }) => {
        // Set the drawerMode and drawerOpened
        if (matchingAliases.includes('md')) {
          this.orientation = 'horizontal';
        }
        else {
          this.orientation = 'vertical';
        }
        // Mark for check
        this.chr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.submitting$.next();
    this.submitting$.complete();
  }
}